import React, { useState } from "react";
import { X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import MediaPicker from "@/components/marketplace/MediaPicker";
import { fieldKind, humanize } from "./crudUtils";

const base = "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#071b22] outline-none transition-[border-color,box-shadow] focus:border-[#165a71] focus:ring-4 focus:ring-[#165a71]/10";

const toLocal = (v) => {
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function TagsInput({ value, onChange, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => { const t = draft.trim(); if (t && !value.includes(t)) onChange([...value, t]); setDraft(""); };
  return (
    <div className={`${base} flex flex-wrap gap-1.5 items-center`}>
      {value.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#165a71]/10 text-[#165a71] px-2.5 py-0.5 text-xs font-medium">
          {String(t)}<button type="button" onClick={() => onChange(value.filter((x) => x !== t))} aria-label={`Remove ${t}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
      <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add} placeholder="Type and press Enter" className="flex-1 min-w-[120px] outline-none text-sm bg-transparent" data-testid={testid} />
    </div>
  );
}

export default function FieldInput({ field, value, original, onChange, readOnly, kind: kindOverride, label: labelText, required, testid: tid }) {
  const kind = kindOverride || fieldKind(field, original ?? value);
  const testid = tid || `edit-${field}`;
  const label = <label className="text-[13px] font-medium text-[#071b22]">{labelText || humanize(field)}{required && <span className="text-rose-500"> *</span>}</label>;

  if (readOnly) return (
    <div>{label}<div className={`${base} bg-slate-50 text-slate-500 font-mono text-xs`} data-testid={testid}>{value == null ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value)}</div></div>
  );
  if (kind === "bool") return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3">
      <div className="text-[13px] font-medium text-[#071b22]">{labelText || humanize(field)}</div>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
  return (
    <div className={kind === "long" || kind === "media" || kind === "json" ? "md:col-span-2" : ""}>
      {label}
      {kind === "media" && <div className="mt-1.5"><MediaPicker value={value ?? ""} onChange={onChange} kind="media_library" testid={testid} /></div>}
      {kind === "tags" && <TagsInput value={value || []} onChange={onChange} testid={testid} />}
      {kind === "json" && <textarea rows={5} defaultValue={JSON.stringify(value, null, 2)} onChange={(e) => { try { onChange(JSON.parse(e.target.value)); } catch (_) {} }} className={`${base} font-mono text-xs`} data-testid={testid} />}
      {kind === "number" && <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={base} data-testid={testid} />}
      {kind === "date" && <input type="datetime-local" value={toLocal(value)} onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)} className={base} data-testid={testid} />}
      {kind === "long" && <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${base} leading-relaxed`} data-testid={testid} />}
      {kind === "text" && <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={testid} />}
    </div>
  );
}
